// /verify-email?token=… (FR-010 to FR-012, FR-042; P-AUTH-1). The code is scrubbed from the address
// first (useLinkToken), then sent once (a ref survives Strict Mode's double effect). Outcomes:
// confirmed ("Your email is confirmed" + Log in, never signed in automatically), link no longer works
// (ask for the email and Send a new link, with a 60 s cooldown, plus Log in), or a connection error
// with Try again re-sending the same in-memory code. An existing session is left alone.
import { Link, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useCooldown } from "@/lib/useCooldown";
import { useSubmitAction } from "@/lib/useSubmitAction";
import { Button } from "@/ui/Button";
import { Form } from "@/ui/Form";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { useAuthApi } from "../AuthApiContext";
import { AUTH_COPY } from "../copy";
import { isAppError } from "@/api/errors";

import { type AuthErrorView, fieldMessage, messageFor } from "../messages";
import { AuthCard } from "../ui/AuthCard";
import { checkEmail } from "../validation";
import { useLinkToken } from "./useLinkToken";

const RESEND_COOLDOWN_S = 60;

type State =
  | { kind: "verifying" }
  | { kind: "confirmed" }
  | { kind: "linkInvalid" }
  | {
      kind: "error";
      view: Exclude<AuthErrorView, { kind: "fields" | "linkInvalid" }>;
    };

const useStyles = makeStyles((t) =>
  StyleSheet.create({ stack: { gap: t.space.lg } }),
);

function LogInLink() {
  return (
    <Link href="/login" asChild>
      <LinkButton role="link">{AUTH_COPY.actions.logIn}</LinkButton>
    </Link>
  );
}

function NewLinkForm() {
  const styles = useStyles();
  const authApi = useAuthApi();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const cooldown = useCooldown(RESEND_COOLDOWN_S);
  const resend = useSubmitAction((address: string) =>
    authApi.resendVerificationEmail(address),
  );

  async function submit() {
    if (cooldown.active) return;
    const e = checkEmail(email);
    if (!e.ok) {
      setError(fieldMessage("email", e.code));
      return;
    }
    setError(null);
    setProblem(null);
    const result = await resend.submit(e.value);
    if (result.status === "done") {
      setSent(true);
      cooldown.start();
    } else if (result.status === "failed") {
      const view = messageFor(result.error, "resendVerificationEmail");
      if (view.kind === "fields") setError(view.fields.email ?? null);
      else if (view.kind !== "linkInvalid") setProblem(view.text);
    }
  }

  return (
    <Form
      onSubmit={() => void submit()}
      accessibilityLabel={AUTH_COPY.actions.sendNewLink}
    >
      <View style={styles.stack}>
        <Text tone="muted">{AUTH_COPY.verifyEmail.linkInvalidHint}</Text>
        {sent ? (
          <InlineMessage tone="success">
            {AUTH_COPY.checkInbox.resent}
          </InlineMessage>
        ) : null}
        {problem ? <InlineMessage tone="error">{problem}</InlineMessage> : null}
        <TextField
          label={AUTH_COPY.fields.email}
          kind="email"
          value={email}
          onChangeText={setEmail}
          error={error}
          testID="verify-email-address"
        />
        <Button
          onPress={() => void submit()}
          pending={resend.pending}
          disabled={cooldown.active}
          testID="verify-email-send"
        >
          {cooldown.active
            ? AUTH_COPY.actions.resendIn(cooldown.remaining)
            : AUTH_COPY.actions.sendNewLink}
        </Button>
      </View>
    </Form>
  );
}

export function VerifyEmailScreen() {
  const styles = useStyles();
  const authApi = useAuthApi();
  const token = useLinkToken();
  const [state, setState] = useState<State>(
    token === null ? { kind: "linkInvalid" } : { kind: "verifying" },
  );
  const router = useRouter();
  const sent = useRef(false);

  const verify = useCallback(async () => {
    if (token === null) return;
    setState({ kind: "verifying" });
    try {
      await authApi.verifyEmail(token);
      setState({ kind: "confirmed" });
    } catch (error) {
      if (!isAppError(error)) throw error;
      const view = messageFor(error, "verifyEmail");
      if (view.kind === "linkInvalid" || view.kind === "fields")
        setState({ kind: "linkInvalid" });
      else setState({ kind: "error", view });
    }
  }, [authApi, token]);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    void verify();
  }, [verify]);

  switch (state.kind) {
    case "verifying":
      return (
        <AuthCard title={AUTH_COPY.verifyEmail.title} testID="verify-email">
          <Text tone="muted">{AUTH_COPY.verifyEmail.verifying}</Text>
        </AuthCard>
      );
    case "confirmed":
      return (
        <AuthCard
          title={AUTH_COPY.verifyEmail.confirmed}
          testID="verify-email-confirmed"
        >
          <Button
            onPress={() => router.replace("/login")}
            testID="verify-email-login"
          >
            {AUTH_COPY.actions.logIn}
          </Button>
        </AuthCard>
      );
    case "linkInvalid":
      return (
        <AuthCard
          title={AUTH_COPY.verifyEmail.linkInvalid}
          testID="verify-email-invalid"
        >
          <View style={styles.stack}>
            <Text>{AUTH_COPY.verifyEmail.alreadyConfirmed}</Text>
            <NewLinkForm />
            <LogInLink />
          </View>
        </AuthCard>
      );
    case "error":
      return (
        <AuthCard
          title={AUTH_COPY.verifyEmail.title}
          testID="verify-email-error"
        >
          <InlineMessage
            tone={state.view.kind === "connection" ? "warning" : "error"}
            detail={
              state.view.kind === "banner" && state.view.reference
                ? AUTH_COPY.errors.reference(state.view.reference)
                : null
            }
          >
            {state.view.text}
          </InlineMessage>
          <Button onPress={() => void verify()} testID="verify-email-retry">
            {AUTH_COPY.actions.tryAgain}
          </Button>
        </AuthCard>
      );
  }
}
